import * as HoverCard from "@radix-ui/react-hover-card";
import { Flex, Text, Button } from "@radix-ui/themes";
import { useCallback, useEffect, useState } from "react";
import { FaChevronRight } from "react-icons/fa";

import { apiClient } from "@/lib/lutely-api/api-request";
import {
  useTracksControllerTracksListGenres,
  useTracksControllerTracksListSubGenres,
} from "@/lib/lutely-api/gen/tracks/tracks";
import { marketplaceTokens } from "@/lib/tokens";
import { cn } from "@/lib/utils";
import FilterCheckbox from "./shared/FilterCheckbox";
import FilterFooter from "./shared/FilterFooter";
import useFilterDropdown from "./shared/useFilterDropdown";
import { MarketplaceFilterKey, MarketplaceUpdates } from "../types";

type GenreFilterProps = {
  parameter?: string;
  onSetFilter: (updates: MarketplaceUpdates) => void;
  onRemoveFilter: (param: MarketplaceFilterKey) => void;
};

export default function GenreFilterHover({
  parameter,
  onSetFilter,
  onRemoveFilter,
}: GenreFilterProps) {
  const { data: genresData } = useTracksControllerTracksListGenres();
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedSubgenres, setSelectedSubgenres] = useState<string[]>([]);
  const [hoveredGenre, setHoveredGenre] = useState<string | null>(null);
  // Persistent map to track which subgenres belong to which genre
  const [subgenreToGenreMap, setSubgenreToGenreMap] = useState<
    Map<string, string>
  >(new Map());

  const { data: subGenresData } = useTracksControllerTracksListSubGenres(
    hoveredGenre || "",
    {
      query: { enabled: !!hoveredGenre },
    },
  );

  const restoreFromParameter = useCallback(() => {
    if (parameter) {
      const allItems = parameter.split(", ");
      const genres: string[] = [];
      const subgenres: string[] = [];
      const genreIds = genresData?.map((g) => g.classificationId) || [];

      allItems.forEach((item) => {
        if (genreIds.includes(item)) {
          genres.push(item);
        } else {
          subgenres.push(item);
        }
      });

      setSelectedGenres(genres);
      setSelectedSubgenres(subgenres);
    } else {
      setSelectedGenres([]);
      setSelectedSubgenres([]);
    }
  }, [parameter, genresData]);

  const {
    open,
    isPinned,
    handleOpenChange,
    handleTriggerClick,
    handleInteractOutside,
    close,
  } = useFilterDropdown({ id: "genre", onRestore: restoreFromParameter });

  // Update the map when we fetch subgenres on hover
  useEffect(() => {
    if (hoveredGenre && subGenresData) {
      setSubgenreToGenreMap((prev) => {
        const updated = new Map(prev);
        subGenresData.forEach((subgenre) => {
          updated.set(subgenre.classificationId, hoveredGenre);
        });
        return updated;
      });
    }
  }, [hoveredGenre, subGenresData]);

  // Clear hover state when dropdown closes
  useEffect(() => {
    if (!open) {
      setHoveredGenre(null);
    }
  }, [open]);

  // Fetch all genre subgenres on mount if we have selected subgenres (for green dot on refresh)
  useEffect(() => {
    async function buildSubgenreMap() {
      if (!genresData || selectedSubgenres.length === 0) return;

      const promises = genresData.map(async (genre) => {
        try {
          const response = await apiClient.get(
            `/tracks/genres/${genre.classificationId}/sub-genres`,
          );
          return { genreId: genre.classificationId, subgenres: response.data };
        } catch (error) {
          return null;
        }
      });

      const results = await Promise.all(promises);

      setSubgenreToGenreMap((prev) => {
        const updated = new Map(prev);
        results.forEach((result) => {
          if (result && result.subgenres) {
            result.subgenres.forEach((subgenre: any) => {
              updated.set(subgenre.classificationId, result.genreId);
            });
          }
        });
        return updated;
      });
    }

    buildSubgenreMap();
  }, [genresData, selectedSubgenres]);

  useEffect(() => {
    restoreFromParameter();
  }, [restoreFromParameter]);

  function toggleGenre(genreId: string) {
    const updated = [...selectedGenres];
    const index = updated.indexOf(genreId);

    if (index >= 0) {
      updated.splice(index, 1);
    } else {
      updated.push(genreId);
    }

    setSelectedGenres(updated);
  }

  function toggleSubgenre(subgenreId: string) {
    const updated = [...selectedSubgenres];
    const index = updated.indexOf(subgenreId);

    if (index >= 0) {
      updated.splice(index, 1);
    } else {
      updated.push(subgenreId);
    }

    setSelectedSubgenres(updated);
  }

  function handleApply() {
    const allSelected = [...selectedGenres, ...selectedSubgenres];

    if (allSelected.length === 0) {
      onRemoveFilter("genre");
    } else {
      onSetFilter({ genre: allSelected.join(", ") });
    }
    close({ restore: false });
  }

  function handleReset() {
    setSelectedGenres([]);
    setSelectedSubgenres([]);
    onRemoveFilter("genre");
    close({ restore: false });
  }

  // Check if a genre has any selected subgenres (for green dot indicator)
  function hasSelectedSubgenres(genreId: string): boolean {
    return selectedSubgenres.some(
      (subgenreId) => subgenreToGenreMap.get(subgenreId) === genreId,
    );
  }

  return (
    <HoverCard.Root
      open={open}
      onOpenChange={handleOpenChange}
      openDelay={isPinned ? 0 : 200}
      closeDelay={isPinned ? 0 : 100}
    >
      <HoverCard.Trigger asChild>
        <Button
          onClick={handleTriggerClick}
          className={cn(
            "flex items-center gap-[8px] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 focus-visible:ring-offset-black",
            marketplaceTokens.filterItem,
            parameter && "!text-white !bg-background-neutral-inverted",
            {
              [marketplaceTokens.filterItemActive]: open,
              [marketplaceTokens.filterItemInactive]: !open,
            },
          )}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-pressed={isPinned}
        >
          <Text className="flex-1">Genre</Text>
          <FaChevronRight
            size={10}
            aria-hidden
            className={cn("transition-transform", {
              "rotate-90": open,
            })}
          />
        </Button>
      </HoverCard.Trigger>

      <HoverCard.Portal>
        <HoverCard.Content
          side="bottom"
          avoidCollisions={false}
          align="start"
          sideOffset={2}
          className="w-[440px] h-[425px] bg-background-neutral-inverted rounded shadow-[0px_10px_18px_-1px_rgba(5,12,43,0.14)] pt-4 px-4 pb-3 flex flex-col gap-4 z-50"
          onInteractOutside={handleInteractOutside}
        >
          <Flex className="flex-1 gap-4 min-h-0">
            {/* Genres Column */}
            <Flex direction="column" className="flex-1 gap-1 min-h-0">
              <Flex
                direction="column"
                className="gap-0.5 overflow-y-auto flex-1 pr-1 filter-dropdown-scroll"
              >
                {genresData?.map((genre) => {
                  const isSelected = selectedGenres.includes(
                    genre.classificationId,
                  );
                  const isHovered = hoveredGenre === genre.classificationId;
                  const hasSubgenres = hasSelectedSubgenres(
                    genre.classificationId,
                  );

                  return (
                    <Flex
                      key={genre.classificationId}
                      onMouseEnter={() =>
                        setHoveredGenre(genre.classificationId)
                      }
                      className={cn(
                        "h-[34px] px-2 rounded flex items-center gap-2 cursor-pointer transition-colors flex-none hover:bg-[#3A404D]/60 hover:text-teal-500",
                        isSelected || isHovered
                          ? "bg-[#3A404D]/60 text-teal-500"
                          : "text-white",
                      )}
                    >
                      <div
                        className="w-4 h-4 relative shrink-0"
                        onClick={() => toggleGenre(genre.classificationId)}
                      >
                        <FilterCheckbox
                          checked={isSelected}
                          hovered={isHovered}
                        />
                      </div>
                      <Text
                        className={cn("text-[14px] font-semibold flex-1", {
                          "text-teal-500": isSelected || isHovered,
                        })}
                        onClick={() => toggleGenre(genre.classificationId)}
                      >
                        {genre.name}
                      </Text>
                      <div className="flex items-center gap-1">
                        {hasSubgenres && (
                          <div className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                        )}
                        <FaChevronRight className="w-3 h-3" />
                      </div>
                    </Flex>
                  );
                })}
              </Flex>
            </Flex>

            {/* Divider */}
            <div className="w-0.5 bg-white/10 rounded-full" />

            {/* Subgenres Column */}
            <Flex direction="column" className="flex-1 gap-1 min-h-0">
              <Text className="text-gray-500 text-[14px] font-normal mb-2">
                {hoveredGenre
                  ? `${genresData?.find((g) => g.classificationId === hoveredGenre)?.name} / Subgenres`
                  : "Subgenres"}
              </Text>
              <Flex
                direction="column"
                className="gap-0.5 overflow-y-auto flex-1 pr-1 filter-dropdown-scroll"
              >
                {hoveredGenre && subGenresData ? (
                  subGenresData.map((subgenre) => {
                    const isSelected = selectedSubgenres.includes(
                      subgenre.classificationId,
                    );
                    return (
                      <Flex
                        key={subgenre.classificationId}
                        onClick={() =>
                          toggleSubgenre(subgenre.classificationId)
                        }
                        className={cn(
                          "h-[34px] px-2 rounded flex items-center gap-2 cursor-pointer transition-colors flex-none text-white hover:bg-[#3A404D]/60 hover:text-teal-500",
                          {
                            "bg-[#3A404D]/60": isSelected,
                          },
                        )}
                      >
                        <FilterCheckbox checked={isSelected} />
                        <Text
                          className={cn("text-[14px] font-semibold", {
                            "text-teal-500": isSelected,
                          })}
                        >
                          {subgenre.name}
                        </Text>
                      </Flex>
                    );
                  })
                ) : (
                  <Text className="text-gray-500 text-[14px] font-normal py-1">
                    Hover over a genre to view available sub-genres
                  </Text>
                )}
              </Flex>
            </Flex>
          </Flex>

          {/* Footer */}
          <FilterFooter
            onReset={handleReset}
            onApply={handleApply}
            variant="bordered"
          />
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
}
